import type { TreeNode, TreeStatusCounts } from '@/types/Tree'
import type { TagColor } from '@/components/app/AppTag.vue'

export const getStatusColor = (status?: string): TagColor => {
    switch (status?.toUpperCase()) {
        case 'UP':
            return 'success'
        case 'DOWN':
            return 'error'
        case 'WARNING':
        case 'ACKNOWLEDGED':
            return 'warning'
        case 'PAUSED':
            return 'info'
        default:
            return 'neutral'
    }
}

export const getStatusDotClass = (status?: string): string => {
    switch (status?.toUpperCase()) {
        case 'UP':
            return 'bg-success shadow-[0_0_8px_color-mix(in_srgb,var(--ui-success)_50%,transparent)]'
        case 'DOWN':
            return 'bg-error shadow-[0_0_8px_color-mix(in_srgb,var(--ui-error)_50%,transparent)] animate-pulse'
        case 'WARNING':
            return 'bg-warning shadow-[0_0_8px_color-mix(in_srgb,var(--ui-warning)_50%,transparent)]'
        case 'ACKNOWLEDGED':
            return 'bg-error'
        case 'PAUSED':
            return 'bg-info shadow-[0_0_8px_color-mix(in_srgb,var(--ui-info)_50%,transparent)]'
        default:
            return 'bg-accented'
    }
}

export const calculateTreeStatusCounts = (nodes: TreeNode[]): TreeStatusCounts => {
    const counts: TreeStatusCounts = {
        total:        0,
        up:           0,
        down:         0,
        warning:      0,
        acknowledged: 0,
        paused:       0,
        unknown:      0,
    }

    const traverse = (node: TreeNode) => {
        if (node.type === 'device' || node.type === 'sensor') {
            counts.total++
            switch (node.status?.toUpperCase()) {
                case 'UP':
                    counts.up++
                    break
                case 'DOWN':
                    counts.down++
                    break
                case 'WARNING':
                    counts.warning++
                    break
                case 'ACKNOWLEDGED':
                    counts.acknowledged++
                    break
                case 'PAUSED':
                    counts.paused++
                    break
                default:
                    counts.unknown++
                    break
            }
        }

        if (node.children?.length) {
            for (const child of node.children) {
                traverse(child)
            }
        }
    }

    for (const node of nodes) {
        traverse(node)
    }

    return counts
}

export const formatUptime = (val: any): string => {
    if (val === null || val === undefined) return '0s'
    const num = Number(val)
    if (isNaN(num)) return String(val)

    const days = Math.floor(num / 86400)
    const hours = Math.floor((num % 86400) / 3600)
    const mins = Math.floor((num % 3600) / 60)
    const secs = Math.floor(num % 60)

    const parts = []
    if (days > 0) parts.push(`${days}d`)
    if (hours > 0 || days > 0) parts.push(`${hours}h`)
    if (mins > 0 || hours > 0 || days > 0) parts.push(`${mins}m`)
    parts.push(`${secs}s`)

    return parts.join(' ')
}
